import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { v4 } from 'uuid';
// i18next
import { useTranslation } from 'react-i18next';
// Hooks
import useQuestionInterval from 'hooks/useQuestionInterval';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { setQuestionReady, setQuestionStart, setAnswers, setQuestionEnd } from 'store/player/playerSlice';
import { selectQuestion, selectQuiz } from 'store/quiz/quizSelectors';
import { selectQuestionEnd } from 'store/player/playerSelectors';
// Components
import { Button } from 'components/Buttons';
import QuestionScoringBar from './QuestionScoringBar';
// Icons
import { FaRegCheckCircle } from "react-icons/fa";
// Utilities
import { shuffle } from 'utilities/Utilities';

type IGridImage = {
  ref: string;
  image: string;
  groupRef: string;
};

const groupSize = 4; // hardcoded group size

const ConnectionsGrid:FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  // State
  const quiz = useAppSelector(selectQuiz);
  const question = useAppSelector(selectQuestion);
  const questionEnd = useAppSelector(selectQuestionEnd);

  const [submittedGroups, setSubmittedGroups] = useState<Record<string, { items: { ref: string }[] }>>({});
  const [selectedImages, setSelectedImages] = useState<IGridImage[]>([]);
  const [title, setTitle] = useState('');

  const groupsLength = useMemo(() => {
    return question.config?.groups?.length || 0;
  }, [question.config?.groups]);

  const submittedGroupsLength = useMemo(() => {
    return Object.keys(submittedGroups).length || 0;
  }, [submittedGroups]);

  const totalScore = useMemo(() => question.score, [question]);
  const scorePerGroup = useMemo(() => totalScore / groupsLength, [totalScore, groupsLength]);
  const score = useMemo(() => {
    if (!submittedGroupsLength) return 0;
    if (submittedGroupsLength === groupsLength) return totalScore;
    return Math.round(scorePerGroup * submittedGroupsLength);
  }, [scorePerGroup, submittedGroupsLength, totalScore, groupsLength]);

  const allImages = useMemo<IGridImage[]>(() => {
    if (!question.config?.groups) return [];

    const flat = question.config.groups.flatMap(group =>
      group.items.map(item => ({
        ref: item.ref,
        image: item.image,
        groupRef: group.ref
      }))
    );

    return shuffle(flat);
  }, [question.config?.groups]);

  const availableImages = useMemo(() => {
    return allImages.filter(
      img => !submittedGroups[img.groupRef]
    );
  }, [allImages, submittedGroups]);

  const onSelect = (image: IGridImage) => {
    setSelectedImages(prev =>
      prev.some(i => i.ref === image.ref)
        ? prev.filter(i => i.ref !== image.ref)
        : [...prev, image]
    );
  };

  const onSubmitGroup = () => {
    if (!selectedImages.length) return;
    const groupRef = selectedImages[0].groupRef;

    const isValidGroup =
      selectedImages.length === groupSize &&
      selectedImages.every(img => img.groupRef === groupRef);

    if (isValidGroup) {
      setSubmittedGroups(prev => ({
        ...prev,
        [groupRef]: {
          items: selectedImages.map(img => ({ ref: img.ref }))
        }
      }));
      const title = question.config?.groups?.find(g => g.ref === groupRef)?.title || '';
      setTitle(title)
    }

    setSelectedImages([]);
  }

  const { width, time } = useQuestionInterval();

  const onSubmit = useCallback(() => {
    if (!Object.keys(submittedGroups).length) return;
    const groups = Object.values(submittedGroups);
    dispatch(setAnswers([{
      type: question.type,
      input: { groups },
      ref: v4(),
      title: '',
      score,
      time,
      correct: true,
      question: {
        ref: question.ref,
        title: question.title
      }
    }] as any));
    // eslint-disable-next-line
  }, [question, submittedGroups, score, time]);

  useEffect(() => {
    dispatch(setQuestionReady(true));
    dispatch(setQuestionStart(true));
    // eslint-disable-next-line
  }, []);

  const skipQuestion = () => {
    dispatch(setQuestionEnd(true));
    dispatch(setQuestionStart(false));
  }

  useEffect(() => {
    if (groupsLength === submittedGroupsLength) skipQuestion();
    // eslint-disable-next-line
  }, [groupsLength, submittedGroupsLength]);

  useEffect(() => {
    return () => {
      if (questionEnd) onSubmit();
    }
  }, [questionEnd, onSubmit]);

  const isDisabled = questionEnd || selectedImages.length === 0 || submittedGroupsLength === groupsLength;
  const isDisabledSkipButton = questionEnd || submittedGroupsLength === groupsLength;

  return (
    <div className="flex-1 px-4" style={{ fontFamily: quiz.questionsSettings.font ? `${quiz.questionsSettings.font}, sans-serif` : 'initial' }}>
      <div
        className="p-1 shadow-primary-border rounded-xl"
        style={{ backgroundColor: quiz.theme.contentBackgroundColor }}
      >
        <p className="text-base text-primary font-bold text-center">
          {question.title}
        </p>
      </div>

      {title && (
        <div className="my-2 flex items-center">
          <div className="relative flex-1 h-[6px] overflow-hidden">
            <div
              className="absolute left-0 top-1/2 h-[2px] w-full -translate-y-1/2 bg-primary"
              style={{
                clipPath: 'polygon(0 50%, 100% 0, 100% 100%)',
                filter: 'drop-shadow(0 0 4px currentColor)',
              }}
            />
          </div>

          <div
            className="py-1 px-4 rounded-xl flex items-center gap-2 border border-green-500"
            style={{ backgroundColor: 'rgba(34, 197, 94, 0.5)' }}
          >
            <p className="text-sm text-primary font-bold text-center">
              {title}
            </p>

            <FaRegCheckCircle className="text-primary text-lg" />
          </div>

          <div className="relative flex-1 h-[6px] overflow-hidden rotate-180">
            <div
              className="absolute left-0 top-1/2 h-[2px] w-full -translate-y-1/2 bg-primary"
              style={{
                clipPath: 'polygon(0 50%, 100% 0, 100% 100%)',
                filter: 'drop-shadow(0 0 4px currentColor)',
              }}
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-4 gap-1 py-4">
        {Object.keys(submittedGroups).map(groupRef => {
          const groupImages = allImages.filter(img => img.groupRef === groupRef);
          return groupImages.map(img => (
            <div key={img.ref} className="relative border border-gray-300 rounded-xl overflow-hidden">
              <img src={img.image} alt="" />
              <div className="absolute top-0 left-0 w-full h-full bg-green-500 opacity-50" />
              <FaRegCheckCircle className="text-white text-xl absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
            </div>
          ))
        })}

        {availableImages.map(image => {
          const isActive = selectedImages.some(i => i.ref === image.ref);

          return (
            <div
              key={image.ref}
              className={`relative border border-gray-300 rounded-xl overflow-hidden ${questionEnd ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              onClick={() => {
                if (questionEnd) return;
                onSelect(image);
              }}
            >
              <img src={image.image} alt="" />
              {isActive ? (
                <div className="absolute top-0 left-0 w-full h-full bg-blue-500 opacity-50" />
              ) : null}
            </div>
          );
        })}
      </div>

      <QuestionScoringBar width={width} score={score} groupsLength={groupsLength} submittedGroupsLength={submittedGroupsLength} />

      <div className="mb-4 sticky -bottom-4">
        <Button
          className="w-full uppercase primary-button"
          onClick={onSubmitGroup}
          disabled={isDisabled}
        >
          {submittedGroupsLength + 1 >= groupsLength ? t('buttons.submit') : t('buttons.next')}
        </Button>
      </div>
      <Button
        className="w-full uppercase primary-button"
        onClick={skipQuestion}
        disabled={isDisabledSkipButton}
      >
        {t('buttons.passQuestion')}
      </Button>
    </div>
  );
}

export default ConnectionsGrid;
